import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import CommentsThread from "../components/CommentsThread";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import {
  ClipboardCheck, Stethoscope, Activity, CalendarClock, ChevronDown,
  AlertCircle, ThumbsUp, ThumbsDown, Star, X, FileText, CheckCircle, Users,
} from "lucide-react";

const REC_TONE = {
  strongly_support:    "pm-pill-green",
  support:             "pm-pill-blue",
  neutral:             "pm-pill-slate",
  prefer_alternative:  "pm-pill-red",
};

const SEV_TONE = {
  minor:      "pm-pill-amber",
  moderate:   "pm-pill-blue",
  long_term:  "pm-pill-red",
};

const MEDICAL_STATUS_TONE = {
  pending_schedule: "pm-pill-slate",
  scheduled:        "pm-pill-blue",
  completed:        "pm-pill-green",
};

const OUTCOME_TONE = {
  pass:        "pm-pill-green",
  conditional: "pm-pill-amber",
  fail:        "pm-pill-red",
};

export default function StaffHome() {
  const [data, setData] = useState(null);
  const [assessFit, setAssessFit] = useState(null);
  const load = () => api.get("/staff/home").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <Layout title="Staff suite" subtitle="Tactical fit · training · medical">
        <div className="text-slate-500" data-testid="staff-loading">Loading staff workspace…</div>
      </Layout>
    );
  }

  const role = data.user.role;
  const isCoach = role === "head_coach" || role === "asst_coach";
  const isMedical = role === "club_doctor" || role === "physio";

  return (
    <Layout title="Staff suite" subtitle={`${data.user.name} · ${role.replace("_", " ")}`}>
      <div className="space-y-6" data-testid="staff-home-root">
        {isCoach && (
          <>
            {/* Tactical fit requests */}
            <section data-testid="tactical-fit-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Director request</div>
                  <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2"><ClipboardCheck size={18} /> Tactical fit assessments</h2>
                </div>
              </div>
              <div className="pm-card divide-y divide-slate-100">
                {(data.tactical_fits || []).map((t) => (
                  <div key={t.id} className="p-4 flex items-start gap-3" data-testid={`fit-${t.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 shrink-0 font-display font-bold text-xs">{t.position}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold">{t.candidate_name}</span>
                        <span className={`pm-pill text-[10px] ${t.status === "assessed" ? "pm-pill-green" : "pm-pill-amber"}`}>{t.status}</span>
                        {t.recommendation && <span className={`pm-pill text-[10px] ${REC_TONE[t.recommendation]}`}>{t.recommendation.replace("_", " ")}</span>}
                        {t.fit_rating != null && (
                          <span className="inline-flex items-center gap-0.5 text-amber-500 ml-auto">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <Star key={i} size={11} fill={i <= t.fit_rating ? "currentColor" : "none"} />
                            ))}
                          </span>
                        )}
                      </div>
                      {t.positional_fit && <div className="text-[12px] text-slate-700 mt-1.5"><b>Positional:</b> {t.positional_fit}</div>}
                      {t.pressing_fit && <div className="text-[12px] text-slate-700"><b>Pressing:</b> {t.pressing_fit}</div>}
                      {t.build_up_role && <div className="text-[12px] text-slate-700"><b>Build-up:</b> {t.build_up_role}</div>}
                      {t.status === "requested" && (
                        <button
                          onClick={() => setAssessFit(t)}
                          className="pm-btn-primary h-8 px-3 rounded-lg text-[11px] font-bold mt-2 inline-flex items-center gap-1.5"
                          data-testid={`assess-${t.id}`}
                        >
                          <ClipboardCheck size={11} /> Assess fit
                        </button>
                      )}
                      <CommentsThread recordType="tactical_fit" recordId={t.id} testid={`comments-fit-${t.id}`} />
                    </div>
                  </div>
                ))}
                {(data.tactical_fits || []).length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="fits-empty">No requests yet.</div>}
              </div>
            </section>

            {/* Training schedule today */}
            <section data-testid="training-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Today</div>
                  <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2"><CalendarClock size={18} /> Training schedule</h2>
                </div>
              </div>
              <div className="pm-card divide-y divide-slate-100">
                {(data.training_today || []).map((s) => (
                  <div key={s.id} className="p-4 flex items-center gap-3" data-testid={`training-${s.id}`}>
                    <div className="font-mono-tab font-bold text-slate-800 w-14">{s.time}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold">{s.title}</div>
                    </div>
                    <span className={`pm-pill text-[10px] ${s.intensity === "High" ? "pm-pill-red" : s.intensity === "Medium" ? "pm-pill-amber" : "pm-pill-green"}`}>{s.intensity}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Squad availability — coach's return-to-training panel */}
            <section data-testid="availability-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Coach view</div>
                  <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2"><Users size={18} /> Squad availability & return-to-training</h2>
                </div>
              </div>
              <div className="pm-card divide-y divide-slate-100">
                {(data.squad_availability || []).length === 0 && (
                  <div className="p-6 text-center text-sm text-slate-500" data-testid="availability-empty">All players available — no current restrictions.</div>
                )}
                {(data.squad_availability || []).map((p) => {
                  const tone =
                    p.clearance === "no_contact" ? "pm-pill-red"
                    : p.clearance === "limited"  ? "pm-pill-amber"
                    : "pm-pill-green";
                  return (
                    <div key={p.player_id} className="p-4 flex items-center gap-3" data-testid={`avail-${p.player_id}`}>
                      <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0"
                           style={{ backgroundImage: p.photo_url ? `url(${p.photo_url})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm truncate">{p.player_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{p.position || "—"} · {p.note || "Cleared for full sessions"}</div>
                      </div>
                      <span className={`pm-pill text-[10px] ${tone}`} data-testid={`avail-clearance-${p.player_id}`}>{p.clearance.replace("_", " ")}</span>
                      {p.eta && <span className="font-mono-tab text-[10px] text-slate-500 ml-2">ETA {fmtDate(p.eta)}</span>}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {isMedical && (
          <>
            {/* Medical clearances */}
            <section data-testid="medical-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Pre-signing</div>
                  <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2"><Stethoscope size={18} /> Medical clearances</h2>
                </div>
              </div>
              <div className="pm-card divide-y divide-slate-100">
                {(data.medical_clearances || []).map((m) => (
                  <div key={m.id} className="p-4 flex items-start gap-3" data-testid={`med-${m.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-700 shrink-0"><Stethoscope size={14} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold">{m.candidate_name}</span>
                        <span className={`pm-pill text-[10px] ${MEDICAL_STATUS_TONE[m.status]}`}>{m.status.replace("_", " ")}</span>
                        {m.outcome && <span className={`pm-pill text-[10px] ${OUTCOME_TONE[m.outcome]}`}>{m.outcome}</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">Requested by {m.requested_by}{m.scheduled_for ? ` · ${fmtDate(m.scheduled_for)}` : ""}{m.assessor ? ` · ${m.assessor}` : ""}</div>
                      {m.confidential_summary && (
                        <div className="text-[11px] text-slate-700 mt-1.5 bg-slate-50 border border-slate-200 rounded-lg p-2 italic">
                          <b>Confidential summary (medical only):</b> {m.confidential_summary}
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        {m.status === "pending_schedule" && (
                          <button
                            onClick={async () => {
                              const date = prompt("Schedule medical for which date? (YYYY-MM-DD)", new Date(Date.now() + 7*86400_000).toISOString().slice(0, 10));
                              if (!date) return;
                              await api.patch(`/staff/medical-clearance/${m.id}`, { scheduled_for: date, status: "scheduled" });
                              load();
                            }}
                            className="pm-btn-primary h-7 px-2.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1"
                            data-testid={`med-schedule-${m.id}`}
                          ><CalendarClock size={10} /> Schedule</button>
                        )}
                        {m.status === "scheduled" && (
                          <button
                            onClick={async () => {
                              const outcome = prompt("Outcome: pass / conditional / fail", "pass");
                              if (!["pass", "conditional", "fail"].includes(outcome)) return;
                              const summary = prompt("Confidential medical summary (visible to medical staff only):", "All vitals normal, fit to sign.");
                              await api.patch(`/staff/medical-clearance/${m.id}`, { status: "completed", outcome, confidential_summary: summary });
                              load();
                            }}
                            className="pm-btn-secondary h-7 px-2.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1"
                            data-testid={`med-complete-${m.id}`}
                          ><CheckCircle size={10} /> Mark complete</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(data.medical_clearances || []).length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="medical-empty">No pending medicals.</div>}
              </div>
            </section>

            {/* Injury list */}
            <section data-testid="injuries-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Squad health</div>
                  <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2"><Activity size={18} /> Injury list</h2>
                </div>
              </div>
              <div className="pm-card divide-y divide-slate-100">
                {(data.injuries || []).map((inj) => (
                  <div key={inj.id} className="p-4 flex items-start gap-3" data-testid={`inj-${inj.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-700 shrink-0"><Activity size={14} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold">{inj.player_name}</span>
                        <span className={`pm-pill text-[10px] ${SEV_TONE[inj.severity]}`}>{inj.severity.replace("_", " ")}</span>
                        {inj.return_clearance && <span className="pm-pill pm-pill-slate text-[10px]">{inj.return_clearance.replace("_", " ")}</span>}
                      </div>
                      <div className="text-[12px] text-slate-700 mt-0.5"><b>Dx:</b> {inj.diagnosis}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Reporter {inj.reporter}{inj.estimated_return_date ? ` · ETA ${fmtDate(inj.estimated_return_date)}` : ""}{inj.days_out ? ` · ${inj.days_out}d out` : ""}
                        {inj.reported_to_director && <span className="text-emerald-700 font-bold"> · sent to Director</span>}
                      </div>
                      <CommentsThread recordType="injury" recordId={inj.id} testid={`comments-injury-${inj.id}`} />
                    </div>
                  </div>
                ))}
                {(data.injuries || []).length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="injuries-empty">No active injuries.</div>}
              </div>
            </section>
            {/* Sessions today + recent clearances */}
            <section data-testid="sessions-clearances-section">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="pm-card p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2 flex items-center gap-1.5"><CalendarClock size={11} /> Scheduled sessions today</div>
                  <div className="space-y-2">
                    {(data.sessions_today || []).length === 0 && (
                      <div className="text-xs text-slate-500" data-testid="sessions-today-empty">No medical sessions on the books today.</div>
                    )}
                    {(data.sessions_today || []).map((s) => (
                      <div key={s.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid={`session-${s.id}`}>
                        <div className="font-bold">{s.candidate_name}</div>
                        <span className="font-mono-tab text-xs text-slate-500">{(s.scheduled_for || "").slice(11, 16) || "TBC"}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pm-card p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2 flex items-center gap-1.5"><CheckCircle size={11} /> Recent clearances issued</div>
                  <div className="space-y-2">
                    {(data.recent_clearances || []).length === 0 && (
                      <div className="text-xs text-slate-500" data-testid="recent-clearances-empty">No clearances issued recently.</div>
                    )}
                    {(data.recent_clearances || []).slice(0, 5).map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid={`recent-clearance-${c.id}`}>
                        <div className="min-w-0 flex-1 truncate">
                          <div className="font-bold truncate">{c.candidate_name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{c.assessor || "—"}</div>
                        </div>
                        <span className={`pm-pill text-[10px] ${OUTCOME_TONE[c.outcome] || "pm-pill-slate"}`}>{c.outcome || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        <div className="pm-card p-4 flex items-start gap-3 border-l-4 border-l-emerald-400 bg-emerald-50/30">
          <AlertCircle size={14} className="text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-700">
            Your account is scoped to the <b>staff suite</b>. {isCoach ? "You see tactical-fit requests from the Director, training schedule, and squad health summaries." : "You see medical clearances, injury reports, and a confidential medical summary that is hidden from non-medical roles. Only severity, ETA, and return clearance are shared with the Director."} Player contracts and agent financials are not visible to your role.
          </div>
        </div>
      </div>

      {assessFit && (
        <AssessFitModal
          fit={assessFit}
          onClose={() => setAssessFit(null)}
          onSaved={() => { setAssessFit(null); load(); }}
        />
      )}
    </Layout>
  );
}

function AssessFitModal({ fit, onClose, onSaved }) {
  const [f, setF] = useState({
    fit_rating: 3, positional_fit: "", pressing_fit: "", build_up_role: "",
    recommendation: "support", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.patch(`/staff/tactical-fit/${fit.id}`, {
        ...f,
        fit_rating: Number(f.fit_rating),
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="assess-fit-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Tactical fit assessment</div>
            <h3 className="font-display text-xl font-black tracking-tight">{fit.candidate_name} · {fit.position}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">Fit rating (1–5)</div>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => set("fit_rating", n)}
                  className={`text-2xl ${n <= f.fit_rating ? "text-amber-500" : "text-slate-300"}`}
                  data-testid={`fit-star-${n}`}
                ><Star size={26} fill={n <= f.fit_rating ? "currentColor" : "none"} /></button>
              ))}
            </div>
          </div>
          <Field label="Positional fit"><textarea className="pm-input" rows={2} value={f.positional_fit} onChange={(e) => set("positional_fit", e.target.value)} /></Field>
          <Field label="Pressing system fit"><textarea className="pm-input" rows={2} value={f.pressing_fit} onChange={(e) => set("pressing_fit", e.target.value)} /></Field>
          <Field label="Build-up role"><textarea className="pm-input" rows={2} value={f.build_up_role} onChange={(e) => set("build_up_role", e.target.value)} /></Field>
          <Field label="Recommendation">
            <select className="pm-input" value={f.recommendation} onChange={(e) => set("recommendation", e.target.value)} data-testid="fit-recommendation">
              <option value="strongly_support">Strongly support</option>
              <option value="support">Support</option>
              <option value="neutral">Neutral</option>
              <option value="prefer_alternative">Prefer alternative</option>
            </select>
          </Field>
        </div>
        {err && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{String(err)}</div>}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" onClick={onClose}>Cancel</button>
          <button
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold disabled:opacity-50"
            disabled={busy}
            onClick={submit}
            data-testid="fit-submit-btn"
          >
            {busy ? "Saving…" : "Send to Director"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
